/* eslint-disable import/no-default-export, import/order, max-lines, max-lines-per-function, no-nested-ternary, react/no-unescaped-entities, react/prop-types */
import { isMediumDeviceAndUp } from "common/utils/browser";
import UseSvg from "common/vue/components/UseSvg.react.vue";
import debounce from "lodash.debounce";
// @ts-expect-error TS(6133): 'React' is declared but its value is never read.
import React, { useEffect, useMemo, useRef, useState } from "react";
import { cn } from "tailwind-variants";
import { VueTeleport } from "common/vue/VueTeleport";
import { useDtfStore } from "../store";
import ArtistEditsHeader from "./ArtistEditsHeader.vue";
import ArtistEditsInformation from "./ArtistEditsInformation.vue";
import { useMarkdownRenderer } from "./composables/useMarkdownRenderer";
import { trackTransfersPinningSessionEntryInput } from "./transfersPinningAnalytics";
import "./PinningSessionEntry.scss";

export default function PinningSessionEntry({ index, artwork, ...restProps }: any) {
  /* TODO(vue-migration): useMarkdownRenderer may be a Vue composable — verify it works as a React hook */
  const { renderMarkdown } = useMarkdownRenderer();
  const store = useDtfStore();

  const isDesktop = isMediumDeviceAndUp();

  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [inputMessage, setInputMessage] = useState("");
  const [isCancelling, setIsCancelling] = useState(false);
  const [isHowItWorksOpen, setIsHowItWorksOpen] = useState(false);
  const [isDesktopPopoverVisible, setIsDesktopPopoverVisible] = useState(false);
  const [desktopPopoverStyle, setDesktopPopoverStyle] = useState({});

  const inputMessageRef = useRef("");
  const artworkRef = useRef(artwork);
  artworkRef.current = artwork;
  const textareaRef = useRef(null);
  const completedHowItWorksBtnRef = useRef(null);
  /* TODO(vue-migration): inputHeaderRef exposes howItWorksButtonRef from ArtistEditsHeader — verify useImperativeHandle is set up in that component */
  const inputHeaderRef = useRef(null);
  const desktopPopoverElRef = useRef(null);
  const popoverHideTimeoutRef = useRef(null);
  const pendingButtonRectRef = useRef<any>(null);

  function isLoading() {
    // @ts-expect-error TS(4111): Property 'pinningSessionLoading' comes from an ind... Remove this comment to see the full error message
    return store.pinningSessionLoading;
  }

  function isEnhancementInProgress() {
    if (!artwork?.id || artwork.isSimulated) return true;
    if (artwork.isLoading) return true;
    // @ts-expect-error TS(4111): Property 'isStandardArtwork' comes from an index s... Remove this comment to see the full error message
    return store.isStandardArtwork && !artwork.enhancementCompleted;
  }

  function isApprovedSession() {
    return artwork.pinningSessionState === "approved";
  }

  function getHowItWorksButton() {
    // @ts-expect-error TS(2339): Property 'howItWorksButtonRef' does not exist on t... Remove this comment to see the full error message
    return completedHowItWorksBtnRef.current ?? inputHeaderRef.current?.howItWorksButtonRef ?? null;
  }

  useEffect(() => {
    if (!isDesktopPopoverVisible || !pendingButtonRectRef.current) return;
    const el = desktopPopoverElRef.current;
    if (!el) return;
    const { rect, left, margin } = pendingButtonRectRef.current;
    pendingButtonRectRef.current = null;
    // @ts-expect-error TS(2339): Property 'offsetHeight' does not exist on type 'ne... Remove this comment to see the full error message
    const popoverHeight = el.offsetHeight;
    const buttonCenterY = rect.top + rect.height / 2;
    const idealTop = buttonCenterY - popoverHeight / 2;
    const clampedTop = Math.max(
      margin,
      Math.min(idealTop, window.innerHeight - popoverHeight - margin)
    );
    setDesktopPopoverStyle({ left: `${left}px`, top: `${clampedTop}px` });
  }, [isDesktopPopoverVisible]);

  function showDesktopPopover() {
    // @ts-expect-error TS(2345): Argument of type 'null' is not assignable to param... Remove this comment to see the full error message
    clearTimeout(popoverHideTimeoutRef.current);
    const $button = getHowItWorksButton();
    if ($button) {
      const rect = $button.getBoundingClientRect();
      const popoverWidth = 580;
      const gap = 8;
      const margin = 8;
      let left = rect.left - popoverWidth - gap;
      if (left < margin) {
        left = rect.right + gap;
      }
      left = Math.max(margin, Math.min(left, window.innerWidth - popoverWidth - margin));
      setDesktopPopoverStyle({
        left: `${left}px`,
        top: "0px",
        visibility: "hidden"
      });
      pendingButtonRectRef.current = { rect, left, margin };
      setIsDesktopPopoverVisible(true);
      return;
    }
    setIsDesktopPopoverVisible(true);
  }

  function hideDesktopPopover() {
    // @ts-expect-error TS(2322): Type 'number' is not assignable to type 'null'.
    popoverHideTimeoutRef.current = setTimeout(() => {
      setIsDesktopPopoverVisible(false);
    }, 150);
  }

  function openHowItWorksModal() {
    if (!isDesktop) {
      setIsHowItWorksOpen(true);
    }
  }

  function closeHowItWorks() {
    setIsHowItWorksOpen(false);
  }

  function handleContainerClick(e: any) {
    if (e.target.closest("button, input, label, a")) {
      return;
    }
    const buttons = Array.from(e.currentTarget.querySelectorAll("button, input, label, a"));
    for (const el of buttons) {
      // @ts-expect-error TS(2571): Object is of type 'unknown'.
      const rect = el.getBoundingClientRect();
      if (
        e.clientX >= rect.left &&
        e.clientX <= rect.right &&
        e.clientY >= rect.top &&
        e.clientY <= rect.bottom
      ) {
        return;
      }
    }
    // @ts-expect-error TS(2339): Property 'focus' does not exist on type 'never'.
    textareaRef.current?.focus();
  }

  const debouncedTrackEntryInput = useMemo(
    () =>
      debounce(() => {
        if (artworkRef.current?.id) {
          trackTransfersPinningSessionEntryInput({
            artworkId: artworkRef.current.id,
            inputValue: inputMessageRef.current
          });
        }
      }, 500),
    []
  );

  useEffect(
    () => () => {
      debouncedTrackEntryInput.cancel();
    },
    []
  );

  /* TODO(vue-migration): handleSubmitMessage reads store.showPinningModal after async await — possible stale closure */
  async function handleSubmitMessage() {
    const trimmed = inputMessage.trim();
    if (!trimmed || isLoading() || isEnhancementInProgress()) return;
    // @ts-expect-error TS(2571): Object is of type 'unknown'.
    await store.openPinningSession(index, trimmed);
    // @ts-expect-error TS(4111): Property 'showPinningModal' comes from an index si... Remove this comment to see the full error message
    if (store.showPinningModal) {
      setInputMessage("");
      inputMessageRef.current = "";
    }
  }

  function handleKeyDown(e: any) {
    if (e.key === "Enter" && !e.shiftKey && !e.metaKey && !e.ctrlKey) {
      e.preventDefault();
      handleSubmitMessage();
    }
  }

  function handleRemove() {
    setIsCancelling(true);
    // @ts-expect-error TS(2571): Object is of type 'unknown'.
    store.clearPinningSession(index);
  }

  function confirmCancel() {
    handleRemove();
    setShowConfirmModal(false);
  }

  return (
    <div
      {...restProps}
      className={cn("scope-07d4245e w-full", restProps.className)}
      data-testid="pinning-session-entry"
    >
      {artwork.pinningSessionId ? (
        <div
          className={cn(
            "scope-07d4245e",
            "flex flex-col gap-3 rounded-lg p-2 md:p-3",
            artwork.pinningSessionState === "approved" ? "bg-[#AD47FF]/20" : "bg-[#E1E4FD]"
          )}
          data-testid="completed-session-indicator"
        >
          {artwork.pinningSessionState === "approved" ? (
            <div className="scope-07d4245e flex items-center gap-2">
              <UseSvg
                className="scope-07d4245e h-8 w-8 shrink-0"
                icon="sparkles-gradient"
                data-testid="h-8"
              />
              <div className="scope-07d4245e">
                <p className="scope-07d4245e text-[15px] font-medium leading-6 text-[#0a0a0a]">
                  AI Edit Applied
                </p>
                <p className="scope-07d4245e text-[11px] font-medium leading-[18px] text-gray-900">
                  {" "}
                  Your design has been updated by AI.{" "}
                </p>
              </div>
            </div>
          ) : (
            <div className="scope-07d4245e flex items-center gap-2">
              <UseSvg
                className="scope-07d4245e h-8 w-8 shrink-0"
                icon="sparkles-gradient"
                data-testid="h-8-1"
              />
              <div className="scope-07d4245e flex-1">
                <div className="scope-07d4245e flex justify-between gap-1.5">
                  <div className="scope-07d4245e flex items-center gap-1.5">
                    <p className="scope-07d4245e text-[15px] font-medium leading-6 text-[#0a0a0a]">
                      Your Artist Edits
                    </p>
                    <span className="scope-07d4245e rounded-full border border-white bg-[#00834f] px-1.5 py-0.5 text-xs font-medium text-white">
                      {" "}
                      Free{" "}
                    </span>
                  </div>
                  <button
                    className="scope-07d4245e cursor-pointer border-0 bg-transparent p-0 text-xs !text-blue-700 underline"
                    ref={completedHowItWorksBtnRef}
                    type="button"
                    data-testid="how-it-works-button"
                    onClick={openHowItWorksModal}
                    onMouseEnter={() => {
                      isDesktop && showDesktopPopover();
                    }}
                    onMouseLeave={() => {
                      isDesktop && hideDesktopPopover();
                    }}
                  >
                    {" "}
                    How it works{" "}
                  </button>
                </div>
                <p className="scope-07d4245e text-[11px] font-medium leading-[18px] text-gray-900">
                  {" "}
                  Human artists will handle your edit after checkout.{" "}
                </p>
              </div>
            </div>
          )}
          {artwork.pinningSummary ? (
            <div
              className="scope-07d4245e rounded-lg bg-white/70 p-3 text-xs font-medium text-gray-900"
              data-testid="markdown-content"
            >
              <div
                className="markdown-rendered scope-07d4245e text-inherit"
                dangerouslySetInnerHTML={{ __html: renderMarkdown(artwork.pinningSummary) }}
              />
            </div>
          ) : null}
          <div className="scope-07d4245e flex gap-2 self-end">
            <button
              className="scope-07d4245e shrink-0 cursor-pointer rounded-[8px] border-[1.5px] border-[#1e2939] bg-white px-3.5 py-2 text-sm font-medium text-gray-900 [font-family:inherit] hover:bg-gray-50 disabled:pointer-events-none disabled:opacity-25"
              type="button"
              disabled={isCancelling}
              data-testid="cancel-design-request-button"
              onClick={() => {
                setShowConfirmModal(true);
              }}
            >
              {artwork.pinningSessionState === "approved" ? "Undo AI Edit" : "Cancel Request"}
            </button>
          </div>
        </div>
      ) : (
        <div
          className={cn(
            "scope-07d4245e",
            // @ts-expect-error TS migration
            isEnhancementInProgress() && "cursor-not-allowed opacity-60"
          )}
        >
          <div className="scope-07d4245e rounded-t-lg bg-[#AD47FF]/20 px-2 pb-4 pt-[3px] md:px-3">
            <ArtistEditsHeader
              className="scope-07d4245e"
              ref={inputHeaderRef}
              title="Edit with AI"
              onHowItWorksClick={openHowItWorksModal}
              onHowItWorksMouseenter={() => {
                isDesktop && showDesktopPopover();
              }}
              onHowItWorksMouseleave={() => {
                isDesktop && hideDesktopPopover();
              }}
              data-testid="artist-edits-header"
            />
          </div>
          <div
            className="gradient-border-purple-blue scope-07d4245e relative z-10 -mt-3 flex cursor-text flex-col gap-2 rounded-lg border-[1.5px] border-transparent px-2 py-1.5 md:px-3 md:py-2"
            onClick={handleContainerClick}
            data-testid="gradient-border-purple-blue"
          >
            <textarea
              className="mp-unmask border-0! outline-none! scope-07d4245e min-h-0 resize-none p-0 text-base placeholder:text-gray-400 disabled:cursor-not-allowed disabled:opacity-60 md:text-sm"
              ref={textareaRef}
              value={inputMessage}
              onChange={e => {
                setInputMessage(e.target.value);
                inputMessageRef.current = e.target.value;
                // @ts-expect-error TS(2554): Expected 0 arguments, but got 1.
                debouncedTrackEntryInput(e);
              }}
              data-testid="request-designer-edit-input"
              placeholder="Tell us what you'd like changed."
              // @ts-expect-error TS(2322): Type 'string' is not assignable to type 'number'.
              rows="2"
              // @ts-expect-error TS(2322): Type 'unknown' is not assignable to type 'boolean ... Remove this comment to see the full error message
              disabled={isLoading() || isEnhancementInProgress()}
              onKeyDown={handleKeyDown}
            />
            <div className="scope-07d4245e flex cursor-pointer justify-end" data-testid="flex">
              <button
                className={cn(
                  "scope-07d4245e",
                  "bg-gradient-purple-blue h-8 min-w-12 cursor-pointer rounded-lg border-0 px-2 !text-xs !font-semibold leading-4 !text-white [font-family:inherit] disabled:pointer-events-none disabled:opacity-25",
                  !inputMessage.trim() && !isLoading() && "opacity-50"
                )}
                type="button"
                data-testid="request-designer-edit-submit"
                // @ts-expect-error TS(2322): Type 'unknown' is not assignable to type 'boolean ... Remove this comment to see the full error message
                disabled={isLoading() || isEnhancementInProgress()}
                onClick={handleSubmitMessage}
              >
                {isLoading() ? "Launching\u2026" : "Launch"}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* @ts-expect-error TS(2786): 'VueTeleport' cannot be used as a JSX component. */}
      <VueTeleport className="scope-07d4245e" to="body" data-testid="teleport">
        {isDesktop && isDesktopPopoverVisible ? (
          <div
            className="scope-07d4245e fixed z-[10000] max-h-[90vh] w-[580px] overflow-y-auto rounded-lg border border-gray-200 bg-white p-4 text-sm shadow-lg"
            ref={desktopPopoverElRef}
            style={desktopPopoverStyle}
            onMouseEnter={() => {
              showDesktopPopover();
            }}
            onMouseLeave={() => {
              hideDesktopPopover();
            }}
            data-testid="fixed"
          >
            <ArtistEditsInformation
              className="scope-07d4245e"
              data-testid="artist-edits-information"
            />
          </div>
        ) : null}
      </VueTeleport>
      {/* @ts-expect-error TS(2786): 'VueTeleport' cannot be used as a JSX component. */}
      <VueTeleport className="scope-07d4245e" to="body" data-testid="teleport-1">
        {!isDesktop && isHowItWorksOpen ? (
          <div className="scope-07d4245e fixed inset-0 z-[10000] flex items-center justify-center">
            <div
              className="scope-07d4245e absolute inset-0 bg-black/60"
              data-testid="how-it-works-backdrop"
              onClick={closeHowItWorks}
            />
            <div className="scope-07d4245e relative max-h-[95dvh] w-full max-w-[360px] overflow-y-auto rounded-lg bg-white px-[15px] py-[15px] pb-[max(15px,env(safe-area-inset-bottom))]">
              <ArtistEditsInformation
                className="scope-07d4245e"
                isMobile={true}
                onClose={closeHowItWorks}
                data-testid="artist-edits-information-1"
              />
            </div>
          </div>
        ) : null}
      </VueTeleport>
      {showConfirmModal ? (
        <div
          className="scope-07d4245e fixed inset-0 z-[10001] flex items-center justify-center bg-black/50"
          data-testid="cancel-confirm-modal"
          onClick={() => {
            setShowConfirmModal(false);
          }}
        >
          <div
            className="scope-07d4245e w-full max-w-sm rounded-lg bg-white p-0 shadow-xl"
            onClick={e => {
              e.stopPropagation();
            }}
            data-testid="w-full"
          >
            <div className="scope-07d4245e border-b border-gray-200 px-6 py-4">
              <h3 className="scope-07d4245e m-0 text-base font-semibold text-gray-700">
                {isApprovedSession() ? "Undo AI Edit?" : "Cancel Artist Request?"}
              </h3>
            </div>
            <div className="scope-07d4245e px-6 pb-6 pt-4 text-center">
              <p className="scope-07d4245e m-0 mb-4 text-base font-medium text-gray-700">
                {isApprovedSession()
                  ? "This will revert your design to the un-edited image. This can\u2019t be undone."
                  : "Your edit request will be removed and won\u2019t be sent to our artists. You can always request it again."}
              </p>
              <div className="scope-07d4245e flex flex-col gap-2">
                <button
                  className="scope-07d4245e w-full cursor-pointer rounded-[8px] border border-red-600 bg-white px-3.5 py-2 text-base font-semibold [font-family:inherit]"
                  type="button"
                  data-testid="cancel-confirm-button"
                  style={{ color: "#dc2626" }}
                  onClick={confirmCancel}
                >
                  {isApprovedSession() ? "Yes, revert to original" : "Yes, cancel request"}
                </button>
                <button
                  className="scope-07d4245e w-full cursor-pointer rounded-[8px] border border-[#414192] bg-[#414192] px-3.5 py-2 text-base font-semibold !text-white [font-family:inherit]"
                  type="button"
                  data-testid="cancel-keep-button"
                  onClick={() => {
                    setShowConfirmModal(false);
                  }}
                >
                  {isApprovedSession() ? "Keep AI edit" : "Keep request"}
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
